import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { ActivatedRoute, ParamMap, Params, Router } from "@angular/router";
import { IViolationsViewModel } from "src/app/models/viewModels/violationsViewModel";
import { IInventoryResourceViewModel } from "src/app/models/viewModels/inventoryResourceViewModel";
import { AiAgentStepActivityTableRowViewModel } from "src/app/models/viewModels/aiAgentStepActivityTableRowViewModel";
import { AiFindingsTableRowViewModel } from "src/app/models/viewModels/aiFindingsTableRowViewModel";
import { AgentStepModel } from "src/app/models/api/agentStep.model";
import { IssuesTableRowViewModel } from "src/app/models/viewModels/issuesTableRowViewModel";
import { QueryParams } from "../../../models/navigation/navigationParams.interface";
import { SidePanelComponent } from "../components/side-panel.component";
import { SidePanelQueryParamsState } from "../side-panel-query-params-state";
import { SidePanelViewModel } from "../types/side-panel";
import { PanelLayer } from "../types/panel-layer";
import { AiPreventionMockTableRowViewModel } from "src/app/models/viewModels/aiPreventionMockTableRowViewModel";

@Injectable({
    providedIn: "root",
})
export class SidePanelService {
    private _sidePanel: SidePanelComponent;
    private _state = new SidePanelQueryParamsState();

    constructor(
        private _router: Router,
        private _activeRoute: ActivatedRoute,
        @Inject(DOCUMENT) private _document: Document,
    ) {}

    public set panel(panel: SidePanelComponent) {
        this._sidePanel = panel;
        setTimeout(() =>
            this._activeRoute.queryParamMap.subscribe((params) => {
                this._updateSidePanelStateV2(params);
            }),
        );
    }

    public isInsideSidePanel(target: HTMLElement): boolean {
        return this._sidePanel.isInside(target);
    }

    public close() {
        if (this._sidePanel.layers().length === 0 && !this._sidePanel.isOpen) {
            return;
        }
        this._closeAllPanels();
    }

    public closePanel(relatedParams: QueryParams[]) {
        this._closePanel(relatedParams);
    }

    public openAgentStepDetailsFromFinding(threadId: string, agentStepId: string, agentStep?: AgentStepModel) {
        this._openAsNested(
            {
                tab: "findings",
                stepThreadId: threadId,
                agentStep: agentStepId,
            },
            agentStep,
        );
    }

    public openViolationDetails(violationId: string, integrationId: string, violationView?: IViolationsViewModel) {
        this._openAsMain(
            {
                integration: integrationId,
                violation: violationId,
                resource: null,
            },
            violationView,
        );
    }

    public openResourceDetails(resource: IInventoryResourceViewModel) {
        this._openAsMain(
            {
                integration: resource.resource.integration.id,
                resource: resource.resource.id,
                violation: null,
            },
            resource,
        );
    }

    public openAgentStepDetails(resource: AiAgentStepActivityTableRowViewModel) {
        this._openAsMain(
            {
                tab: "activity",
                stepThreadId: resource.threadId,
                integration: resource.integrationId,
                agentStep: resource.id,
            },
            resource,
        );
    }

    public openFindingDetails(finding: AiFindingsTableRowViewModel) {
        this._openAsMain({ tab: "findings", integration: finding.integrationId, finding: finding.id }, finding);
    }

    public openPreventionDetails(preventionView: AiPreventionMockTableRowViewModel) {
        this._openAsMain(
            {
                tab: "prevention",
                preventionId: preventionView.id,
                integration: preventionView.integrationId,
            },
            preventionView,
        );
    }

    public openIssueDetails(issueView: IssuesTableRowViewModel) {
        const issue = issueView.issue;
        this._openAsMain({ integration: issue.integrationId, issue: issue.id }, issueView);
    }

    private _updateSidePanelStateV2(params: ParamMap) {
        this._state.update(this._router, params);

        const owner = <HTMLElement>this._document.querySelector("table");

        const sidePanels = this._state.getSidePanelsToOpen();
        if (!sidePanels.length) {
            this._sidePanel.closeAllStackedPanels();
            return;
        }

        const layers: PanelLayer[] = sidePanels.map(({ component, inputs, dependsOn, shortcuts }) => {
            return {
                component,
                inputBindings: inputs(this._state.getRouteState(), this._state.viewModel),
                keyboardShortcuts: shortcuts,
                dependsOn,
                dependsOnStringified: JSON.stringify(dependsOn),
            };
        });

        this._sidePanel.openStacked(owner, layers);
    }

    private _openAsMain(queryParams: Params, viewModel?: SidePanelViewModel) {
        this._router.navigate([], {
            state: { viewModel },
            relativeTo: this._activeRoute,
            queryParams: { ...this._state.getEmptyRouteState(), ...queryParams },
            queryParamsHandling: "merge",
        });
    }

    private _openAsNested(queryParams: Params, viewModel?: SidePanelViewModel) {
        this._router.navigate([], {
            state: { viewModel },
            relativeTo: this._activeRoute,
            queryParams: queryParams,
            queryParamsHandling: "merge",
        });
    }

    private _closeAllPanels(): void {
        this._router.navigate([], {
            relativeTo: this._activeRoute,
            queryParams: this._state.getEmptyRouteState(),
            queryParamsHandling: "merge",
        });
    }

    private _closePanel(relatedParams: QueryParams[]): void {
        const nullifiedParams = relatedParams.reduce((prev, curr) => ({ ...prev, [curr]: null }), {} as Params);
        this._router.navigate([], {
            relativeTo: this._activeRoute,
            queryParams: nullifiedParams,
            queryParamsHandling: "merge",
        });
    }
}
